import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import useAccelContext from "../../contexts/AccelContext";

const Badge = ({ text, color }: { text: string; color: string }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "4px 8px",
      borderRadius: "12px",
      backgroundColor: "transparent",
      color: color,
      fontSize: "12px",
      fontWeight: "bold",
      fontFamily: "Helvetica Neue",
      border: `1px solid ${color}`,
    }}
  >
    {text}
  </Box>
);

interface FinancialBoxProps {
  title: string;
  amount: string;
  badgeText: string;
  color: string;
  theme: any; // Replace 'any' with the appropriate type if known
}

const FinancialBox = ({
  title,
  amount,
  badgeText,
  color,
  theme,
}: FinancialBoxProps) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
      paddingLeft: 2,
      paddingRight: 2,
      paddingTop: 0,
      paddingBottom: 0,
      flex: 1,
    }}
  >
    <Typography
      variant="h5"
      sx={{
        fontSize: 18,
        fontFamily: "Helvetica Neue",
        color: theme.text.hex,
        alignSelf: "center",
      }}
    >
      {title}
    </Typography>

    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "100%",
        pt: 2,
        pb: 2,
        justifyContent: "space-between",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontSize: 18,
          fontFamily: "Helvetica Neue",
          color: color,
        }}
      >
        {amount}
      </Typography>
      <Badge text={badgeText} color={color} />
    </Box>
  </Box>
);

const ProfitAndLossBox = () => {
  const { selectedDate, theme, revenueProfitExpense } = useAccelContext();


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatPercentage = (percent: number) => {
    const sign = percent >= 0 ? "+" : "";
    return `${sign}${percent.toFixed(1)}%`;
  };

  if (!revenueProfitExpense) {
    return (
      <Box
        sx={{
          backgroundColor: theme?.accent?.hex || "#000000",
          width: "100%",
          height: "100%", // Set height to one-third of the viewport height
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress sx={{ color: theme?.primary?.hex }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%", // Set height to one-third of the viewport height
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          height: "100%",
        }}
      >
        <FinancialBox
          title="Revenue"
          amount={formatCurrency(revenueProfitExpense?.totalRevenue || 0)}
          badgeText={`${formatPercentage(revenueProfitExpense?.revenueChange || 0)} Since ${selectedDate.subtract(1, "month").format("MMM")}`}
          color={theme?.primary?.hex || "#000000"}
          theme={theme}
        />
        <Box
          sx={{
            width: "100%",
            height: "1px",
            backgroundColor: theme?.secondary?.hex || "#000000",
          }}
        />
        <FinancialBox
          title="Expenses"
          amount={formatCurrency(revenueProfitExpense?.totalExpenses || 0)}
          badgeText={`${formatPercentage(revenueProfitExpense?.expenseChange || 0)} Since ${selectedDate.subtract(1, "month").format("MMM")}`}
          color={theme?.negative?.hex || "#000000"}
          theme={theme}
        />
        <Box
          sx={{
            width: "100%",
            height: "1px",
            backgroundColor: theme?.secondary?.hex || "#000000",
          }}
        />
        <FinancialBox
          title="Profit"
          amount={formatCurrency(revenueProfitExpense?.netProfit || 0)}
          badgeText={`${formatPercentage(revenueProfitExpense?.profitChange || 0)} Since ${selectedDate.subtract(1, "month").format("MMM")}`}
          color={theme?.positive?.hex || "#000000"}
          theme={theme}
        />
      </Box>
    </Box>
  );
};

export default ProfitAndLossBox;
